/**
 * API key panel — three states:
 *   Setup  — first-run, gold-bordered welcome
 *   Unlock — slim navy strip, passphrase only
 *   Active — green-tinted strip, masked key, controls
 */

import { el, toast, openModal } from './dom.js';
import {
  isEncryptedKeyStored,
  getSessionApiKey,
  setupEncryption,
  unlockWithPassphrase,
  changePassphrase,
  forgetEncryptedKey,
  clearSessionApiKey
} from '../lib/crypto.js';
import { setState, getState } from '../state.js';

/**
 * Render the panel. Returns the panel node. The caller (folder stage) is
 * responsible for placing it.
 */
export function renderApiKeyPanel(onChange) {
  const sessionKey = getSessionApiKey();
  const stored = isEncryptedKeyStored();

  if (sessionKey) return renderActive(sessionKey, onChange);
  if (stored)     return renderUnlock(onChange);
  return renderSetup(onChange);
}

const lockIcon = () => svg(`<path d="M5 9V7a5 5 0 0 1 10 0v2" /><rect x="3.5" y="9" width="13" height="9" rx="1.5" ry="1.5" /><circle cx="10" cy="13.5" r="1.2" />`);
const checkIcon = () => svg(`<path d="M4 10.5l4 4 8-9" stroke-width="2" />`);
const keyIcon = () => svg(`<circle cx="6" cy="10" r="3.5" /><path d="M9 10h8M14 10v3M17 10v2" />`);

function svg(inner) {
  const wrap = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  wrap.setAttribute('viewBox', '0 0 20 20');
  wrap.setAttribute('fill', 'none');
  wrap.setAttribute('stroke', 'currentColor');
  wrap.setAttribute('stroke-width', '1.5');
  wrap.setAttribute('stroke-linecap', 'round');
  wrap.setAttribute('stroke-linejoin', 'round');
  wrap.setAttribute('class', 'api-icon');
  wrap.innerHTML = inner;
  return wrap;
}

/* -------- Setup state -------- */

function renderSetup(onChange) {
  const apiInput  = el('input', {
    type: 'password',
    placeholder: 'sk-ant-…',
    autocomplete: 'off',
    spellcheck: 'false',
    class: 'input-mono'
  });
  const passInput = el('input', {
    type: 'password',
    placeholder: 'choose a passphrase',
    autocomplete: 'off',
    spellcheck: 'false'
  });

  const onSave = async () => {
    const k = apiInput.value.trim();
    const p = passInput.value;
    if (!k) { toast('Paste an API key first.', 'error'); return; }
    if (!p || p.length < 4) { toast('Passphrase must be at least 4 characters.', 'error'); return; }
    try {
      await setupEncryption(k, p);
      toast('Key encrypted and saved on this device.', 'success');
      if (onChange) onChange();
    } catch (err) {
      toast(err.message || 'Could not save key.', 'error', 5000);
    }
  };

  apiInput.addEventListener('keydown',  (e) => { if (e.key === 'Enter') passInput.focus(); });
  passInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') onSave(); });

  return el('div', { class: 'api-panel api-panel-setup' }, [
    el('div', { class: 'api-head' }, [
      keyIcon(),
      el('h3', {}, ['Set up your Anthropic API key'])
    ]),
    el('p', {}, [
      'WiseEntry uses your own Anthropic API key to extract invoice data. The key is encrypted with a passphrase and stored only on this device. ',
      el('strong', {}, ['You\u2019ll type the passphrase at the start of each session to unlock it.'])
    ]),
    el('div', { class: 'api-form' }, [
      el('div', { class: 'field-block' }, [
        el('label', { class: 'field-label' }, ['Anthropic API key']),
        apiInput
      ]),
      el('div', { class: 'field-block' }, [
        el('label', { class: 'field-label' }, ['Passphrase']),
        passInput
      ]),
      el('button', { class: 'primary-btn', onClick: onSave }, ['Save'])
    ])
  ]);
}

/* -------- Unlock state -------- */

function renderUnlock(onChange) {
  const passInput = el('input', {
    type: 'password',
    placeholder: 'Type your passphrase',
    autocomplete: 'off',
    spellcheck: 'false'
  });

  const onUnlock = async () => {
    try {
      await unlockWithPassphrase(passInput.value);
      toast('API key unlocked for this session.', 'success');
      if (onChange) onChange();
    } catch (err) {
      toast(err.message || 'Could not unlock.', 'error');
      passInput.value = '';
      passInput.focus();
    }
  };

  passInput.addEventListener('keydown', (e) => { if (e.key === 'Enter') onUnlock(); });

  setTimeout(() => passInput.focus(), 50);

  return el('div', { class: 'api-panel api-panel-unlock' }, [
    lockIcon(),
    el('span', { class: 'lbl' }, ['Locked']),
    passInput,
    el('button', { class: 'unlock-btn', onClick: onUnlock }, ['Unlock']),
    el('button', {
      class: 'reset-link',
      title: 'Forget the encrypted key on this device',
      onClick: () => {
        if (confirm('Forget the encrypted API key on this device? You will need to paste your key again.')) {
          forgetEncryptedKey();
          toast('Encrypted key removed.', 'info');
          if (onChange) onChange();
        }
      }
    }, ['Reset'])
  ]);
}

/* -------- Active state -------- */

function renderActive(key, onChange) {
  const masked = key.slice(0, 7) + '…' + key.slice(-4);

  return el('div', { class: 'api-panel api-panel-active' }, [
    checkIcon(),
    el('span', { class: 'status' }, ['API key unlocked']),
    el('span', { class: 'masked' }, [masked]),
    el('div', { class: 'api-actions' }, [
      el('button', {
        class: 'mini-btn',
        title: 'Change your passphrase',
        onClick: () => openChangePassphraseModal(onChange)
      }, ['Change passphrase']),
      el('button', {
        class: 'mini-btn',
        title: 'Lock until next time',
        onClick: () => {
          clearSessionApiKey();
          toast('Locked. Type passphrase to unlock again.', 'info');
          if (onChange) onChange();
        }
      }, ['Lock']),
      el('button', {
        class: 'mini-btn danger',
        title: 'Forget the encrypted key on this device',
        onClick: () => {
          if (confirm('Forget the API key on this device? You will need to paste it again.')) {
            forgetEncryptedKey();
            toast('Encrypted key removed. Paste your API key again.', 'info');
            if (onChange) onChange();
          }
        }
      }, ['Forget on this device'])
    ])
  ]);
}

function openChangePassphraseModal(onChange) {
  const cur = el('input', { type: 'password', placeholder: 'current passphrase' });
  const nu  = el('input', { type: 'password', placeholder: 'new passphrase (4+ chars)' });
  const c2  = el('input', { type: 'password', placeholder: 'confirm new passphrase' });

  openModal((close) => el('div', {}, [
    el('div', { class: 'modal-head' }, [
      el('h2', {}, ['Change ', el('em', {}, ['passphrase'])])
    ]),
    el('div', { class: 'modal-body' }, [
      el('div', { class: 'field-group' }, [
        el('label', {}, ['Current passphrase']), cur
      ]),
      el('div', { class: 'field-group' }, [
        el('label', {}, ['New passphrase']), nu
      ]),
      el('div', { class: 'field-group' }, [
        el('label', {}, ['Confirm new passphrase']), c2
      ])
    ]),
    el('div', { class: 'modal-foot' }, [
      el('button', {
        class: 'primary-btn',
        onClick: async () => {
          if (nu.value !== c2.value) { toast('New passphrases don\u2019t match.', 'error'); return; }
          try {
            await changePassphrase(cur.value, nu.value);
            toast('Passphrase changed.', 'success');
            close();
            if (onChange) onChange();
          } catch (err) {
            toast(err.message || 'Could not change passphrase.', 'error');
          }
        }
      }, ['Change']),
      el('button', { class: 'skip-link', onClick: close }, ['Cancel'])
    ])
  ]));
}
